import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  CircularProgress
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  ChevronDown,
  PanelLeftClose,
  PanelRightOpen,
  Info,
  Mail,
  Globe,
  Newspaper,
  MessageSquare,
  Search
} from 'lucide-react';
import { useGlobalConfig } from 'context/GlobalContext';
import { useSelector } from 'react-redux';
import { selectLevel1Data, selectLevel2Data, selectFeatureMetricsLoading, selectFeatureMetricsError } from 'store/dataReducers/featureMetricsSlice';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { useAvailableFuelTypes } from 'hooks/useProjectFuelTypes';
import { useBulkManageData } from 'utils/template-list';
import {
  transformToHierarchicalData,
  getFuelTypeLabel,
  groupWebTemplatesByPage,
  type ChannelWithTemplates,
  type WebPageGroup
} from 'utils/templates-from-feature-metrics';

type TemplateSelection = {
  templateKey: string;
  fuelType?: string;
};

type Props = {
  selectedTemplate?: TemplateSelection;
  onSelect: (template: TemplateSelection) => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  disabled?: boolean; // Disable switching templates while preview is rendering
};

const channelIcons: Record<string, React.ElementType> = {
  Email: Mail,
  HER: Newspaper,
  Web: Globe,
  SMS: MessageSquare,
};

const channelIconFor = (name: string) => {
  const Icon = channelIcons[name];
  return Icon ? <Icon size={16} /> : null;
};

export const TemplatesSidebar: React.FC<Props> = ({ selectedTemplate, onSelect, isOpen = true, onToggleOpen, disabled = false }) => {
  const { rc } = useGlobalConfig();
  const level1Data = useSelector(selectLevel1Data);
  const level2Data = useSelector(selectLevel2Data);
  const loading = useSelector(selectFeatureMetricsLoading);
  const error = useSelector(selectFeatureMetricsError);
  const selectedPilotId = useSelector(selectSelectedPilotId);
  // SDK initialization is no longer required to view Web templates
  // SDK will be initialized on-demand when rendering Web widget previews
  const [searchQuery, setSearchQuery] = React.useState<string>('');

  // Same source as DataScenariosSidebar: API-driven list of project fuel types (e.g. ELECTRIC, GAS, DUAL)
  const { availableFuelTypes: projectFuelTypes } = useAvailableFuelTypes(rc, selectedPilotId, true);
  const fuelType = React.useMemo(() => projectFuelTypes[0] || 'ELECTRIC', [projectFuelTypes]);

  const { data: bulkManageData, loading: bulkManageLoading, error: bulkManageError } = useBulkManageData(rc, selectedPilotId);

  // Same template list as Data Scenarios sidebar (shared transform + same projectFuelTypes source)
  const data = React.useMemo((): ChannelWithTemplates[] | null => {
    if (!level1Data || !Array.isArray(level1Data)) return null;

    const isCdfMetadata = (item: any) =>
      item?.metadata?.fuelType === 'CDF';

    const filteredLevel1 = level1Data.filter(item => !isCdfMetadata(item));
    const filteredLevel2 = Array.isArray(level2Data)
      ? level2Data.filter(item => !isCdfMetadata(item))
      : level2Data;

    return transformToHierarchicalData(
      filteredLevel1,
      filteredLevel2,
      projectFuelTypes,
      false
    );
  }, [level1Data, level2Data, projectFuelTypes]);

  const filteredData = React.useMemo((): ChannelWithTemplates[] | null => {
    if (!data) return null;
    if (!searchQuery.trim()) return data;
    const query = searchQuery.toLowerCase();
    return data
      .map(channel => ({
        ...channel,
        templates: (channel.templates || []).filter(
          t =>
            t.name?.toLowerCase().includes(query) ||
            t.templateKey?.toLowerCase().includes(query)
        )
      }))
      .filter(ch => ch.templates.length > 0);
  }, [data, searchQuery]);

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 0, width: '100%', minWidth: isOpen ? 300 : 48, overflow: 'hidden' }}>
      <CardHeader
        sx={{
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 56
        }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isOpen && (
              <>
                <Typography variant="h6" component="div" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  CX Templates
                </Typography>
                <Tooltip title="These are the templates currently in scope for the project.">
                  <IconButton size="small" sx={{ p: 0.5 }}>
                    <Info size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        }
        action={
          onToggleOpen ? (
            <Tooltip title={isOpen ? 'Collapse Templates bar' : 'Expand Templates bar'} placement={isOpen ? 'top' : 'right'}>
              <IconButton size="small" onClick={onToggleOpen} sx={{ ml: 'auto' }}>
                {isOpen ? <PanelLeftClose size={16} /> : <PanelRightOpen size={16} />}
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <CardContent sx={{ pt: 1.5, px: 2, pb: 2, flex: 1, display: isOpen ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ py: 2, px: 1 }}>{error}</Typography>
        )}

        {!loading && !error && (
          <>
            <TextField
              placeholder="Search templates"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} style={{ color: 'inherit', opacity: 0.7 }} />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 1.5 }}
            />

            {(filteredData || []).map(ch => {
              return (
                <Accordion
                  key={ch.name}
                  disableGutters
                  elevation={0}
                  defaultExpanded={ch.name === 'Email'}
                  sx={{
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ChevronDown size={16} />}
                    sx={{
                      minHeight: 40,
                      '&.Mui-expanded': { minHeight: 40 },
                      px: 1
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.secondary',
                        fontWeight: 600,
                        fontSize: '0.875rem'
                      }}
                    >
                      {channelIconFor(ch.name)}
                      {ch.name}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1 }}>
                  {ch.name === 'Web' ? (
                    // Hierarchical view for Web channel: Page → Widgets
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {groupWebTemplatesByPage(ch.templates || []).map(pageGroup => {
                        // Check if this is a standalone page (only 1 widget and widget name matches page name)
                        const isStandalonePage = pageGroup.widgets.length === 1 &&
                          pageGroup.widgets[0].templateKey === `DASHBOARD.${pageGroup.pageName}`;

                        // For standalone pages, render with same accordion style but make it clickable (not expandable)
                        if (isStandalonePage) {
                          const t = pageGroup.widgets[0];
                          const reactKey = `${t.templateKey}::${t.fuelType || 'UNKNOWN'}`;
                          const active = selectedTemplate?.templateKey === t.templateKey && selectedTemplate?.fuelType === t.fuelType;
                          const fuelLabel = getFuelTypeLabel(t.fuelType);

                          return (
                            <Accordion
                              key={reactKey}
                              disableGutters
                              elevation={0}
                              expanded={false}
                              sx={{
                                '&:before': { display: 'none' },
                                bgcolor: 'transparent',
                                pointerEvents: 'none',
                                mb: 0.5
                              }}
                            >
                              <Tooltip title={t.templateKey} placement="right">
                                <AccordionSummary
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (disabled) return;
                                    onSelect({ templateKey: t.templateKey, fuelType: t.fuelType });
                                  }}
                                  sx={{
                                    minHeight: 32,
                                    '&.Mui-expanded': { minHeight: 32 },
                                    px: 1,
                                    py: 0.5,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    opacity: disabled ? 0.6 : 1,
                                    pointerEvents: 'auto',
                                    '& .MuiAccordionSummary-expandIconWrapper': {
                                      display: 'none'
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={fuelLabel}
                                      sx={{
                                        fontSize: '0.75rem',
                                        height: 20,
                                        width: 70,
                                        px: 0.5,
                                        py: 0.5,
                                        borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                        color: 'primary.main',
                                        bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                        flexShrink: 0,
                                        '& .MuiChip-label': {
                                          textAlign: 'center',
                                          width: '100%',
                                          px: 0
                                        }
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: 500,
                                        color: 'text.secondary',
                                        flex: 1
                                      }}
                                    >
                                      {t.name}
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                              </Tooltip>
                            </Accordion>
                          );
                        }

                        // For pages with multiple widgets, render as accordion
                        // Determine fuel type for the page (use first widget's fuel type)
                        const pageFuelType = pageGroup.widgets.length > 0 ? pageGroup.widgets[0].fuelType : undefined;
                        const pageFuelLabel = getFuelTypeLabel(pageFuelType);

                        return (
                          <Accordion
                            key={pageGroup.pageName}
                            disableGutters
                            elevation={0}
                            defaultExpanded={false}
                            sx={{
                              '&:before': { display: 'none' },
                              '&.Mui-expanded': { margin: 0 },
                              bgcolor: 'transparent',
                              mb: 0.5
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ChevronDown size={14} />}
                              sx={{
                                minHeight: 32,
                                '&.Mui-expanded': { minHeight: 32 },
                                px: 1,
                                py: 0.5
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={pageFuelLabel}
                                  sx={{
                                    fontSize: '0.75rem',
                                    height: 20,
                                    width: 70,
                                    px: 0.5,
                                    py: 0.5,
                                    borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                    color: 'primary.main',
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                    flexShrink: 0,
                                    '& .MuiChip-label': {
                                      textAlign: 'center',
                                      width: '100%',
                                      px: 0
                                    }
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                    flex: 1
                                  }}
                                >
                                  {pageGroup.pageDisplayName}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, pb: 0.5, px: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {pageGroup.widgets.map(t => {
                                // Create composite key for React key uniqueness only
                                const reactKey = `${t.templateKey}::${t.fuelType || 'UNKNOWN'}`;
                                const active = selectedTemplate?.templateKey === t.templateKey && selectedTemplate?.fuelType === t.fuelType;
                                const fuelLabel = getFuelTypeLabel(t.fuelType);

                                return (
                                  <Tooltip key={reactKey} title={t.templateKey} placement="right">
                                    <Box
                                      onClick={() => {
                                        if (disabled) return;
                                        onSelect({ templateKey: t.templateKey, fuelType: t.fuelType });
                                      }}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        gap: 1,
                                        width: '100%',
                                        px: 1.5,
                                        py: 1.5,
                                        ml: 1,
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.6 : 1,
                                        borderRadius: 1,
                                        textAlign: 'left',
                                        border: active ? theme => `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                                        backgroundColor: active
                                          ? theme => theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.primary.main, 0.16)
                                            : alpha(theme.palette.primary.main, 0.08)
                                          : 'transparent',
                                        '&:hover': {
                                          backgroundColor: (theme) =>
                                            disabled
                                              ? 'transparent'
                                              : theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.common.white, 0.08)
                                                : alpha(theme.palette.common.black, 0.04)
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                      }}
                                    >
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        label={fuelLabel}
                                        sx={{
                                          fontSize: '0.75rem',
                                          height: 20,
                                          width: 70,
                                          px: 0.5,
                                          py: 0.5,
                                          borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                          color: 'primary.main',
                                          bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                          flexShrink: 0,
                                          '& .MuiChip-label': {
                                            textAlign: 'center',
                                            width: '100%',
                                            px: 0
                                          }
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          flex: 1,
                                          fontSize: '0.875rem',
                                          textAlign: 'left'
                                        }}
                                      >
                                        {t.name}
                                      </Typography>
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                        );
                      })}
                    </Box>
                  ) : (
                    // Flat list for other channels (Email, HER, SMS)
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {(ch.templates || []).map(t => {
                        const active = selectedTemplate?.templateKey === t.templateKey && selectedTemplate?.fuelType === t.fuelType;
                        // Use displayFuelLabel when available (e.g. 'DF' for USER_WELCOME), else derive from fuelType
                        const fuelLabel = t.displayFuelLabel || getFuelTypeLabel(t.fuelType);
                        const hasFuelType = !!(t.fuelType || t.displayFuelLabel);

                        return (
                          <Tooltip key={t.templateKey} title={t.templateKey} placement="right">
                            <Box
                              onClick={() => {
                                if (disabled) return;
                                onSelect({ templateKey: t.templateKey, fuelType: t.fuelType });
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: 1,
                                width: '100%',
                                px: 1.5,
                                py: 1.5,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                opacity: disabled ? 0.6 : 1,
                                borderRadius: 1,
                                textAlign: 'left',
                                border: active ? theme => `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                                backgroundColor: active
                                  ? theme => theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.primary.main, 0.16)
                                    : alpha(theme.palette.primary.main, 0.08)
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    disabled
                                      ? 'transparent'
                                      : theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.08)
                                        : alpha(theme.palette.common.black, 0.04)
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              {hasFuelType && (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={fuelLabel}
                                  sx={{
                                    fontSize: '0.75rem',
                                    height: 20,
                                    width: 70,
                                    px: 0.5,
                                    py: 0.5,
                                    borderColor: theme => alpha(theme.palette.primary.main, 0.5),
                                    color: 'primary.main',
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
                                    flexShrink: 0,
                                    '& .MuiChip-label': {
                                      textAlign: 'center',
                                      width: '100%',
                                      px: 0
                                    }
                                  }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  flex: 1,
                                  fontSize: '0.875rem',
                                  textAlign: 'left'
                                }}
                              >
                                {t.name}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
              );
            })}
          </>
        )}
        </div>
      </CardContent>
    </Card>
  );
}
